import { STR_ACCEPTED, STR_FAILURE } from "core/constants/strings"
import { useAuth } from "core/context/auth"
import useToast from "core/hooks/useToast"
import { STR_SUCCESS } from "core/constants/strings"
import BatchChangeAPIDatasourceImp from "data/API/Common/BatchChangeAPIDataSourceImpl"
import { BatchChangeRepositoryImpl } from "data/repository/Common/BatchChangeRepositoryImpl"
import { GetAllBatchDetails } from "domain/useCase/Common/BatchChange/GetAllBatches"
import { GetBatchDetails } from "domain/useCase/Common/BatchChange/GetBatch"
import { GetBatchStatusDetails } from "domain/useCase/Common/BatchChange/GetBatchStatus"
import { GetBatchStudentsDetails } from "domain/useCase/Common/BatchChange/GetBatchStudents"
import { UpdateBatchDetails } from "domain/useCase/Common/BatchChange/UpdateBatchDetails"
import { GetAllBatches } from "domain/useCase/Admin/CreateClass/GetAllBatches"
import { UpdateBatchBunch } from "domain/useCase/Admin/UpdateBatch/UpdateBatchBunch"
import { CreateClassAPIDataSourceImpl } from "data/API/Admin/CreateClassAPIDataSourceImpl"
import UpdateBatchAPIDataSourceImpl from "data/API/Admin/UpdateBatchAPIDataSource"
import { CreateClassRepositoryImpl } from "data/repository/Admin/CreateClassRepositoryImpl"
import { UpdateBatchRepositoryImpl } from "data/repository/Admin/UpdateBatchRepositoryImpl"
import React from "react"

export default function BatchViewModel() {
  const { auth } = useAuth()
  const { toast, changeToastDetails, changeToastVisibility } = useToast()
  const [getAllBatchesFilter, setGetAllBatchesFilter] = React.useState<any>([])
  const [getBatchResponse, setGetBatchResponse] = React.useState<any>([])
  const [selectedStudents, setSelectedStudents] = React.useState<string[]>([])
  const [page, setPage] = React.useState<number>(1)
  const [batchstats, setbatchstats] = React.useState<any>([])
  const [hasMore, setHasMore] = React.useState<boolean>(true)
  const [selectBatch, setSelectBatch] = React.useState<any>("")
  const [isLoading, setIsLoading] = React.useState<boolean>(false)
  const [loading, setLoading] = React.useState<any>(false)
  const [search, setSearch] = React.useState<string>("")
  const [expandedBatchId, setExpandedBatchId] = React.useState<string | null>(null)
  const observerRef = React.useRef<IntersectionObserver | null>(null)
  const loaderRef = React.useRef<HTMLDivElement | null>(null)
  const [refreshData, setRefreshData] = React.useState(false)
  const [batchStudentDetails, setBatchStudentDetails] = React.useState<any>({})
  const [selectedBatch, setSelectedBatch] = React.useState(null)
  const [batchList, setBatchList] = React.useState<any>([])
  const [selectAll, setSelectAll] = React.useState(false)
  const [emailFilter, setEmailFilter] = React.useState("")
  const [statusFilter, setStatusFilter] = React.useState("")

  const getBatchStatusUseCase = new GetBatchStatusDetails(
    new BatchChangeRepositoryImpl(new BatchChangeAPIDatasourceImp())
  )
  const getBatchesStudentsUseCase = new GetBatchStudentsDetails(
    new BatchChangeRepositoryImpl(new BatchChangeAPIDatasourceImp())
  )
  const getBatchesUseCase = new GetAllBatchDetails(new BatchChangeRepositoryImpl(new BatchChangeAPIDatasourceImp()))
  const getBatcheUseCase = new GetBatchDetails(new BatchChangeRepositoryImpl(new BatchChangeAPIDatasourceImp()))
  const UpdateBatchStatusUseCase = new UpdateBatchDetails(
    new BatchChangeRepositoryImpl(new BatchChangeAPIDatasourceImp())
  )
  const getAllBatchesUseCase = new GetAllBatches(new CreateClassRepositoryImpl(new CreateClassAPIDataSourceImpl()))
  const updateBatchBunchUseCase = new UpdateBatchBunch(
    new UpdateBatchRepositoryImpl(new UpdateBatchAPIDataSourceImpl())
  )

  interface BatchStats {
    [batchId: string]: {
      totalStudents: number
      pausedStudents: number
      activeStudents: number
    }
  }
  interface BatchStatsResponse {
    success: boolean
    data?: BatchStats
    error?: string
  }
  interface Batch {
    id: any
    Start_Date: string
    isEnrolledAllowed: boolean
  }
  interface BatchResponse {
    success: boolean
    data: {
      batches: Batch[]
    }
  }
  async function updateStudentBatch(previousBatchData: boolean) {
    if (!selectBatch || selectedStudents.length === 0) {
      changeToastDetails(STR_FAILURE, "Please select a batch and at least one student.")
      changeToastVisibility(true)
      return
    }

    setLoading(true)

    try {
      const payload = {
        emails: selectedStudents,
        batch: selectBatch,
      }

      const response = await updateBatchBunchUseCase.invoke(auth, payload)

      if (response.success) {
        changeToastDetails(STR_SUCCESS, "Batch updated successfully!")
      } else {
        changeToastDetails(STR_FAILURE, response.error || "Failed to update batch.")
      }
    } catch (error) {
      console.error("Error updating student batch:", error)
      changeToastDetails(STR_FAILURE, "Unexpected error occurred.")
    } finally {
      setLoading(false)
      changeToastVisibility(true)
    }
  }

  const batchesToDisplay = (search.trim() ? [...getAllBatchesFilter] : [...getBatchResponse]).map((batch) => {
    const stats = batchstats?.[batch.id] || {}

    return {
      ...batch,
      totalStudents: stats.totalStudents ?? 0,
      activeStudents: stats.activeStudents ?? 0,
      pausedStudents: stats.pausedStudents ?? 0,
    }
  })
  async function getBatches() {
    const response = await getAllBatchesUseCase.invoke(auth)

    if (response.success) {
      setBatchList(response.data)
    }
  }
  const formatDate = (dateString: any) => {
    if (!dateString || dateString.length !== 8) return dateString
    const year = dateString.substring(0, 4)
    const month = dateString.substring(4, 6)
    const day = dateString.substring(6, 8)
    return `${day} / ${month} / ${year}`
  }
  const fetchBatchStudentDetails = async (batchId: any): Promise<void> => {
    setIsLoading(true)
    try {
      const response: any = await getBatchesStudentsUseCase.invoke(auth, batchId)
      const studentsData = response.data[batchId] || {}
      setBatchStudentDetails(studentsData)
    } catch (error) {
      console.error("Error fetching batch student details:", error)
      changeToastDetails(STR_FAILURE, error instanceof Error ? error.message : "An unexpected error occurred")
    } finally {
      setIsLoading(false)
    }
  }

  const fetchAllBatches = async (pageNumber = page, limit = 10): Promise<void> => {
    if (isLoading || !hasMore) return

    setIsLoading(true)
    try {
      const response: BatchResponse = await getBatchesUseCase.invoke(auth, pageNumber, limit)
      if (!response?.success) {
        changeToastDetails(STR_FAILURE, "Error Occurred while fetching batches")
        changeToastVisibility(true)
        setIsLoading(false)
        return
      }
      const batches: Batch[] = response.data.batches || []

      if (batches.length === 0) {
        setHasMore(false)
      } else {
        setGetBatchResponse((prev: any) => [...prev, ...batches])
      }
    } catch (error) {
      console.error("Error fetching batches:", error)
      changeToastDetails(STR_FAILURE, "Unexpected error occurred")
      changeToastVisibility(true)
    } finally {
      setIsLoading(false)
    }
  }
  const toggleBatchStatus = async (batchId: string, batch: any) => {
    const newStatus = !batch.isEnrolledAllowed
    setGetBatchResponse((prev: any) =>
      prev.map((b: any) => (b.id === batchId ? { ...b, isEnrolledAllowed: newStatus } : b))
    )

    setGetAllBatchesFilter((prev: any) =>
      prev.map((b: any) => (b.id === batchId ? { ...b, isEnrolledAllowed: newStatus } : b))
    )

    try {
      const response = await UpdateBatchStatusUseCase.invoke(auth, batchId, { isEnrolledAllowed: newStatus })

      if (!response.success) {
        throw new Error(response.error || "Failed to update batch status")
      }
      changeToastDetails(STR_ACCEPTED, "Batch status updated successfully")
      changeToastVisibility(true)
    } catch (error) {
      console.error("Error updating batch status:", error)
      setGetBatchResponse((prev: any) =>
        prev.map((b: any) => (b.id === batchId ? { ...b, isEnrolledAllowed: batch.isEnrolledAllowed } : b))
      )

      setGetAllBatchesFilter((prev: any) =>
        prev.map((b: any) => (b.id === batchId ? { ...b, isEnrolledAllowed: batch.isEnrolledAllowed } : b))
      )
      changeToastDetails(STR_FAILURE, "Unexpected error occurred")
      changeToastVisibility(true)
    }
  }

  const searchBatchByName = async (name: string): Promise<void> => {
    setIsLoading(true)
    try {
      if (name.trim() === "") {
        setGetAllBatchesFilter([])
        return
      }

      const response: BatchResponse = await getBatcheUseCase.invoke(auth, name)
      if (!response?.success) {
        changeToastDetails(STR_FAILURE, "Error Occurred while searching batches")
        changeToastVisibility(true)
        setIsLoading(false)
        return
      }

      const batches: Batch[] = response.data.batches || []
      setGetAllBatchesFilter(batches)
    } catch (error) {
    } finally {
      setIsLoading(false)
    }
  }
  const GetBatchStats = async (auth: any, batchId?: string): Promise<void> => {
    setIsLoading(true)
    try {
      if (!batchId) {
        throw new Error("Batch ID is required")
      }

      const response: BatchStatsResponse = await getBatchStatusUseCase.invoke(auth, batchId)

      if (!response?.success) {
        throw new Error(response?.error || "Error occurred while getting BatchStats")
      }

      if (!response.data || !response.data[batchId]) {
        throw new Error("No batch statistics data found")
      }

      const batchData = response.data[batchId]

      setbatchstats((prev: any) => ({
        ...prev,
        [batchId]: {
          totalStudents: batchData.totalStudents ?? 0,
          activeStudents: batchData.activeStudents ?? 0,
          pausedStudents: batchData.pausedStudents ?? 0,
        },
      }))
    } catch (error) {
      console.error("Error fetching batch stats:", error)
      changeToastDetails(STR_FAILURE, error instanceof Error ? error.message : "An unexpected error occurred")
      changeToastVisibility(true)
    } finally {
      setIsLoading(false)
    }
  }
  const handleKeyPress = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      await handleSearch()
    }
  }
  const handleDropdownToggle = async (batchId: string) => {
    if (expandedBatchId === batchId) {
      setExpandedBatchId(null)
    } else {
      setExpandedBatchId(batchId)

      if (!batchstats[batchId]) {
        await GetBatchStats(auth, batchId)
      }
    }
  }

  const handleSearch = async () => {
    if (search.trim()) {
      setGetAllBatchesFilter([])
      await searchBatchByName(search)
      setRefreshData(!refreshData)
    } else {
      setGetAllBatchesFilter([])
    }
  }
  const handleClearAll = () => {
    setSelectedStudents([])
    setSelectAll(false)
  }

  const handleSelectStudent = (id: any) => {
    if (selectedStudents.includes(id)) {
      setSelectedStudents(selectedStudents.filter((studentId) => studentId !== id))
    } else {
      setSelectedStudents([...selectedStudents, id])
    }
  }

  const getStatusColor = (status: any) => {
    switch (status) {
      case "active":
        return "text-green-500"
      case "paused":
        return "text-yellow-500"
      case "Opt-out":
        return "text-red-500"
      default:
        return ""
    }
  }
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedStudents([])
    } else {
      const allStudentEmails = filteredStudents
        .filter((student: any) => student.status !== "paused" && student.status !== "Opt-out")
        .map((student: any) => student.email)
      setSelectedStudents(allStudentEmails)
    }
    setSelectAll(!selectAll)
  }
  const filteredStudents = batchStudentDetails
    ? Object.entries(batchStudentDetails)
        .map(([email, data]: any) => ({
          email,
          ...data,
        }))
        .filter((student) => {
          const matchesEmail = emailFilter ? student.email.toLowerCase().includes(emailFilter.toLowerCase()) : true
          const matchesStatus = statusFilter ? student.status.toLowerCase().includes(statusFilter.toLowerCase()) : true
          return matchesEmail && matchesStatus
        })
    : []

  return {
    auth,
    page,
    toast,
    search,
    refreshData,
    batchstats,
    loaderRef,
    observerRef,
    getBatchResponse,
    getAllBatchesFilter,
    batchStudentDetails,
    isLoading,
    loading,
    expandedBatchId,
    batchList,
    hasMore,
    batchesToDisplay,
    selectedBatch,
    selectedStudents,
    selectBatch,
    selectAll,
    emailFilter,
    statusFilter,
    filteredStudents,
    handleSelectAll,
    handleClearAll,
    handleSelectStudent,
    getStatusColor,
    setStatusFilter,
    setEmailFilter,
    setSelectAll,
    changeToastDetails,
    updateStudentBatch,
    setSelectBatch,
    getBatches,
    setSelectedStudents,
    setSelectedBatch,
    fetchBatchStudentDetails,
    setRefreshData,
    setPage,
    setSearch,
    GetBatchStats,
    handleSearch,
    formatDate,
    fetchAllBatches,
    handleDropdownToggle,
    handleKeyPress,
    setGetAllBatchesFilter,
    toggleBatchStatus,
    changeToastVisibility,
    searchBatchByName,
  }
}
